/**
 * 集計表示 (BG全体 × 配下の内訳が複数) での需要想定値の直接編集
 * (spec demand-forecast-daily.md AC-DFD-004d / 054 / 054a、ADR
 * demand-forecast-retailer-level-editing)。
 *
 * 編集の SoT は小売単位のまま、集計値への変更を各内訳の現在値の比率で按分して
 * 内訳の未保存バッファへ書き戻す。按分後の値は丸めない (AC-DFC-EDIT-003)。
 * 結果は内訳ごとの通常の保存・確定フローに乗り、保存前に画面で確認できる
 * (セル編集・最大電力補正と同じく frontend の編集バッファ上の変換)。
 */
import {
  calcBgTotalSlots,
  computePeak,
} from '~/pages/DemandForecastDaily/models/slotHelpers'
import type { KomaSlots } from '~/pages/DemandForecastDaily/models/types'

const KOMA_COUNT = 48

/**
 * Converts the raw aggregate editor text at the Connect boundary without a
 * lossy parseFloat round-trip.  Aggregate intents are int64 and the existing
 * number editor can only represent safe integers.
 */
export function parseAggregateInteger(raw: string): number | undefined {
  if (!/^-?\d+$/.test(raw)) return undefined
  const value = BigInt(raw)
  const max = BigInt(Number.MAX_SAFE_INTEGER)
  if (value < -max || value > max) return undefined
  return Number(value)
}

/**
 * 合計を 0 とみなす許容誤差 (kWh)。正負の小数が打ち消し合う合計 (0.1 + 0.2 - 0.3) は
 * 浮動小数では厳密な 0 にならないため、比率が作れないコマとして同じく拒否する。
 */
const ZERO_TOTAL_EPSILON = 1e-9

/** 集計対象の内訳 1 件 (業務上は小売電気事業者)。 */
export type AggregateBreakdown = {
  retailerBusinessCd: string
  displayName: string
  forecastSlots: KomaSlots
}

/**
 * GC 前のコマ (koma > gcKoma = 今から編集できるコマ) に空 (未入力) のコマが 1 つでも
 * ある内訳を返す (AC-DFD-004d、2026-10-13 PdM 判断で GC 済みコマは判定から外す。
 * GC 済みコマは按分もしないため、空でも按分の基準が欠けない)。0 は入力済みとして
 * 扱う (空セルと 0 の区別、AC-DFD-004)。
 */
export function findUnfilledBreakdowns<T extends AggregateBreakdown>(
  breakdowns: readonly T[],
  gcKoma: number,
): T[] {
  const firstEditable = Math.max(gcKoma, 0)
  return breakdowns.filter((b) => {
    for (let i = firstEditable; i < KOMA_COUNT; i++) {
      if (b.forecastSlots[i] === undefined) return true
    }
    return false
  })
}

/**
 * 未入力の内訳名を表示用に並べる (spec 付録の AC-DFD-004d 文言)。日本語は「・」、
 * 英語は `A and B` / `A, B, and C` で並べる。
 */
export function formatBreakdownNames(
  names: readonly string[],
  language: string,
): string {
  if (!language.startsWith('en')) return names.join('・')
  return new Intl.ListFormat('en', {
    style: 'long',
    type: 'conjunction',
  }).format(names)
}

export type AggregateDistribution = {
  /** 値が変わった内訳だけの按分後スロット (retailerBusinessCd → slots)。 */
  slotsByRetailer: Map<string, KomaSlots>
  /**
   * 内訳の合計が 0 のため変更を受け付けなかったコマ番号 (1-indexed, AC-DFD-054a)。
   * 全内訳が 0 の時に加え、正負が打ち消し合って合計 0 の時も比率が作れないため含む
   * (2026-10-13 PdM 判断)。
   */
  rejectedKomas: number[]
}

/**
 * 集計値の変更 (nextTotal) を、各内訳の当該コマの現在値の比率で按分する
 * (AC-DFD-054)。
 *
 * - 集計値が変わっていないコマ・GC 済みコマ (koma <= gcKoma) は変えない
 * - 値を消したコマ (nextTotal が undefined) は按分できないため変えない
 * - 全内訳の合計が 0 のコマは比率が作れないため変えず rejectedKomas に積む
 *   (AC-DFD-054a)。正負が打ち消し合って合計 0 のコマも同じく拒否する (2026-10-13
 *   PdM 判断)。一部の内訳だけ 0 のコマは比率どおり (0 の内訳は 0 のまま)
 * - 按分後の値は丸めない。浮動小数の誤差で合計が入力値からずれないよう、
 *   残差は按分後の値が最も大きい内訳に寄せて Σ(内訳) = 入力値 を保つ
 */
export function distributeAggregateSlots(
  breakdowns: readonly AggregateBreakdown[],
  nextTotal: KomaSlots,
  gcKoma: number,
): AggregateDistribution {
  const next = new Map<string, KomaSlots>()
  const rejectedKomas: number[] = []

  for (let koma = 1; koma <= KOMA_COUNT; koma++) {
    if (gcKoma > 0 && koma <= gcKoma) continue
    const i = koma - 1
    const target = nextTotal[i]
    if (target === undefined) continue

    const values = breakdowns.map((b) => b.forecastSlots[i])
    // 未入力の内訳があるコマは按分の比率が作れない (呼び出し元が AC-DFD-004d で
    // 編集自体を止めるが、ここでも値を作らない)。
    if (values.some((v) => v === undefined)) continue
    const current = values.reduce<number>((sum, v) => sum + (v ?? 0), 0)
    if (target === current) continue
    if (Math.abs(current) < ZERO_TOTAL_EPSILON) {
      rejectedKomas.push(koma)
      continue
    }

    const distributed = values.map((v) => ((v ?? 0) * target) / current)
    const residual = target - distributed.reduce((sum, v) => sum + v, 0)
    if (residual !== 0) {
      let largest = 0
      for (let j = 1; j < distributed.length; j++) {
        if (
          Math.abs(distributed[j] ?? 0) > Math.abs(distributed[largest] ?? 0)
        ) {
          largest = j
        }
      }
      distributed[largest] = (distributed[largest] ?? 0) + residual
    }

    for (const [j, b] of breakdowns.entries()) {
      const slots = next.get(b.retailerBusinessCd) ?? [...b.forecastSlots]
      slots[i] = distributed[j]
      next.set(b.retailerBusinessCd, slots)
    }
  }

  return { slotsByRetailer: next, rejectedKomas }
}

/**
 * 集計表示の補正モード「編集コマ以降へ比例配分」(AC-DFD-006) を集計値に適用する。
 * 編集コマを newValue にし、以降のコマ (GC 済みと空を除く) に変化率
 * (newValue ÷ 編集前の集計値) を掛ける。按分の前段なので丸めない
 * (AC-DFC-EDIT-003。小売表示の `applyProportional` は整数に丸める既存実装)。
 * 編集前の集計値が空・0 なら以降のコマは変えない。
 */
export function applyProportionalToTotal(
  total: KomaSlots,
  nextSlots: KomaSlots,
  editedKoma: number,
  newValue: number,
  gcKoma: number,
): KomaSlots {
  const next = [...nextSlots]
  next[editedKoma - 1] = newValue
  const orig = total[editedKoma - 1]
  if (orig === undefined || orig === 0) return next
  const ratio = newValue / orig
  for (let koma = editedKoma + 1; koma <= KOMA_COUNT; koma++) {
    if (gcKoma > 0 && koma <= gcKoma) continue
    const v = total[koma - 1]
    if (v === undefined) continue
    next[koma - 1] = v * ratio
  }
  return next
}

/**
 * 集計表示での最大電力補正 (AC-DFD-054、AC-DFC-EDIT-001): 集計値のピークが
 * targetPeak になる倍率を、配下の全内訳の需要想定値に同じく掛ける。GC 済みコマ
 * と空コマは変えず、全内訳が 0 のコマは 0 のまま。按分後の値は丸めない
 * (AC-DFC-EDIT-003)。集計値のピークが 0 以下なら倍率が作れないため変えない。
 */
export function rescaleAggregateToPeak(
  breakdowns: readonly AggregateBreakdown[],
  targetPeak: number,
  gcKoma: number,
): Map<string, KomaSlots> {
  const next = new Map<string, KomaSlots>()
  const total = calcBgTotalSlots(breakdowns.map((b) => b.forecastSlots))
  const currentPeak = computePeak(total, gcKoma)
  if (currentPeak <= 0) return next
  const factor = targetPeak / currentPeak
  for (const b of breakdowns) {
    next.set(
      b.retailerBusinessCd,
      b.forecastSlots.map((v, i) => {
        const koma = i + 1
        if (v === undefined) return v
        if (gcKoma > 0 && koma <= gcKoma) return v
        return v * factor
      }),
    )
  }
  return next
}
